import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import authService from '../services/authService';

/**
 * Frontend-Backend Connection Test Component
 * Tests various connectivity scenarios between frontend and backend
 */
const ConnectionTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);

  const tests = [
    {
      name: 'Backend Health Check',
      test: async () => {
        const response = await fetch('http://localhost:8080/health');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
      }
    },
    {
      name: 'API Client Configuration',
      test: async () => {
        // Test if apiClient is properly configured
        const baseURL = apiClient.defaults.baseURL;
        const timeout = apiClient.defaults.timeout;
        return { baseURL, timeout, configured: !!baseURL };
      }
    },
    {
      name: 'OpenAI Health Check',
      test: async () => {
        const response = await apiClient.get('/openai/health');
        return response.data;
      }
    },
    {
      name: 'Authentication Test (Login)',
      test: async () => {
        try {
          const result = await authService.login('admin@hraisystem.com', 'admin123');
          return { success: true, user: result.user, hasToken: !!result.token };
        } catch (error) {
          throw new Error(`Auth failed: ${error.message}`);
        }
      }
    },
    {
      name: 'Protected Route Test (Get Current User)',
      test: async () => {
        const result = await authService.getCurrentUser();
        return { user: result, authenticated: true };
      }
    },
    {
      name: 'Candidates API Test',
      test: async () => {
        const response = await apiClient.get('/candidates');
        return { 
          success: true, 
          candidateCount: response.data?.data?.length || 0,
          hasData: !!response.data 
        };
      }
    }
  ];

  const runTest = async (testConfig) => {
    const startTime = Date.now();
    try {
      const result = await testConfig.test();
      const duration = Date.now() - startTime;
      return {
        name: testConfig.name,
        success: true,
        result,
        duration,
        error: null
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      return {
        name: testConfig.name,
        success: false,
        result: null,
        duration,
        error: error.message
      };
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setTestResults([]);
    
    const results = [];
    
    for (const test of tests) {
      const result = await runTest(test);
      results.push(result);
      setTestResults([...results]); // Update UI after each test
      
      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setIsRunning(false);
  };

  const getStatusColor = (success) => {
    return success ? 'text-green-600' : 'text-red-600';
  };

  const getStatusIcon = (success) => {
    return success ? '✅' : '❌';
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Frontend-Backend Connection Test
      </h2>
      
      <div className="mb-6">
        <button
          onClick={runAllTests}
          disabled={isRunning}
          className={`px-6 py-3 rounded-lg font-medium ${
            isRunning 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {isRunning ? 'Running Tests...' : 'Run Connection Tests'}
        </button>
      </div>

      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700">Test Results:</h3>
          
          {testResults.map((result, index) => (
            <div 
              key={index}
              className={`p-4 border rounded-lg ${
                result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className={`font-medium ${getStatusColor(result.success)}`}>
                  {getStatusIcon(result.success)} {result.name}
                </h4>
                <span className="text-sm text-gray-500">
                  {result.duration}ms
                </span>
              </div>
              
              {result.success ? (
                <div className="text-sm text-green-700">
                  <strong>Success!</strong>
                  {result.result && (
                    <pre className="mt-2 p-2 bg-green-100 rounded text-xs overflow-auto">
                      {JSON.stringify(result.result, null, 2)}
                    </pre>
                  )}
                </div>
              ) : (
                <div className="text-sm text-red-700">
                  <strong>Error:</strong> {result.error}
                </div>
              )}
            </div>
          ))}
          
          {!isRunning && testResults.length === tests.length && (
            <div className="mt-6 p-4 bg-gray-100 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2">Test Summary:</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-green-600">✅ Passed: </span>
                  {testResults.filter(r => r.success).length}
                </div>
                <div>
                  <span className="text-red-600">❌ Failed: </span>
                  {testResults.filter(r => !r.success).length}
                </div>
              </div>
              
              {testResults.some(r => !r.success) && (
                <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded">
                  <h5 className="font-medium text-yellow-800 mb-2">Troubleshooting:</h5>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• Make sure the backend server is running on port 8080</li>
                    <li>• Check if the database is connected and migrations are applied</li>
                    <li>• Verify environment variables are properly configured</li>
                    <li>• Check browser console for additional error details</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectionTest;